import React, { useState, useEffect } from 'react';
import { useApiClient } from '../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from '@mui/material';
import { AccountBalance, TrendingUp, Pending, CheckCircle, Refresh } from '@mui/icons-material';
import { format } from 'date-fns';
import ConnectAccountSetup from './ConnectAccountSetup';

/**
 * EarningsTab Component
 * Shows participant earnings overview:
 * - Total earnings
 * - Pending payouts
 * - Payout history
 * - Connected account setup
 */
const EarningsTab: React.FC = () => {
  const apiClient = useApiClient();

  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState<any>(null);
  const [payoutHistory, setPayoutHistory] = useState<any[]>([]);
  const [connectedAccount, setConnectedAccount] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch earnings summary
      const earningsResponse = await apiClient.getUserEarnings();
      if (earningsResponse.success) {
        setEarnings(earningsResponse.data);
      }

      // Fetch payout history
      const historyResponse = await apiClient.getUserPayoutHistory(20);
      if (historyResponse.success) {
        setPayoutHistory(historyResponse.data || []);
      }

      // Check for connected account
      const accountResponse = await apiClient.getCurrentUserConnectedAccount();
      if (accountResponse.success && accountResponse.data) {
        setConnectedAccount(accountResponse.data);
      }
    } catch (err) {
      setError('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'usd'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
      case 'in_transit':
        return 'info';
      case 'failed':
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle fontSize="small" />;
      case 'pending':
      case 'in_transit':
        return <Pending fontSize="small" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Show account setup if no connected account
  if (!connectedAccount) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Earnings & Payouts
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Set up your payout account to receive bounty winnings.
        </Typography>
        <ConnectAccountSetup onAccountConnected={fetchData} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Earnings & Payouts</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Earnings Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
        <div>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingUp color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Total Earnings
                </Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {earnings
                  ? formatCurrency(earnings.totalEarnings || 0, earnings.currency)
                  : '$0.00'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All-time bounty winnings
              </Typography>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" />
                <Typography variant="subtitle2" color="text.secondary">
                  Completed Payouts
                </Typography>
              </Box>
              <Typography variant="h4">
                {earnings
                  ? formatCurrency(earnings.totalPayouts || 0, earnings.currency)
                  : '$0.00'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Successfully received
              </Typography>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Pending color="info" />
                <Typography variant="subtitle2" color="text.secondary">
                  Pending Payouts
                </Typography>
              </Box>
              <Typography variant="h4">
                {earnings
                  ? formatCurrency(earnings.pendingPayouts || 0, earnings.currency)
                  : '$0.00'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                In progress
              </Typography>
            </CardContent>
          </Card>
        </div>
      </Box>

      {/* Payout History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payout History
          </Typography>

          {payoutHistory.length === 0 ? (
            <Box py={4} textAlign="center">
              <AccountBalance sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No payouts yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Win bounties to receive payouts!
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Arrival Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payoutHistory.map((payout) => (
                    <TableRow key={payout.id}>
                      <TableCell>
                        {format(new Date(payout.created_at), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(payout.amount, payout.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payout.status}
                          color={getStatusColor(payout.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap maxWidth="300px">
                          {payout.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {payout.arrival_date
                          ? format(new Date(payout.arrival_date), 'MMM dd, yyyy')
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Account Info */}
      <Box mt={3}>
        <ConnectAccountSetup />
      </Box>
    </Box>
  );
};

export default EarningsTab;
